import { ControlledAsyncDataGrid } from "src/components/common/dataGrid/ControlledAsyncDataGrid";
import { useClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/useClientSideDataGridModel";
import {
	PartConfigurationBomPricesApi,
	PartConfigurationBomPricesApi_BomLineGroupPricesDto,
} from "src/api/generated/erp/configurator/api/partConfigurationBomPricesApi.ts";
import { floatColumn, textColumn } from "src/components/common/dataGrid/columns";
import i18n from "i18next";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { faArrowDown } from "@fortawesome/pro-regular-svg-icons";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { PartConfigurationBomPricesOfGroupDataGrid } from "src/components/views/erp/configurator/inspecting/PartConfigurationBomPricesOfGroupDataGrid.tsx";
import { PartConfigurationDetailsViewApi_PartConfigurationDto } from "src/api/generated/erp/configurator/api/partConfigurationDetailsViewApi.ts";
import { calculateSalesMarginPercent, formatLabelWithCurrencyCode } from "src/components/views/erp/sales/salesUtils.ts";
import { formatMoneyWithCurrency } from "src/utils/numberUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface PartConfigurationBomPricesByGroupsDataGridProps {
	partConfiguration: PartConfigurationDetailsViewApi_PartConfigurationDto;
	vatPercent?: number;
}

export const PartConfigurationBomPricesByGroupsDataGrid = ({
	partConfiguration,
	vatPercent,
}: PartConfigurationBomPricesByGroupsDataGridProps) => {
	const showCostInSellingCurrency = partConfiguration.sellingCurrencyId !== partConfiguration.domesticCurrencyId;
	const useDomesticCostForMargin = partConfiguration.sellingCurrencyId === partConfiguration.domesticCurrencyId;

	const { dataGridProps, onlySelectedRow } = useClientSideDataGridModel({
		fetchData: () =>
			PartConfigurationBomPricesApi.getBomPricesByBomLineGroupRecursively({
				partConfigurationId: partConfiguration.partConfigurationId,
			}),
		getRowId: (row) => row.bomLineGroupId ?? -1,
		gridId: "A2F3D8E9C4F4808E",
		initialParams: {},
	});

	const { openDialog } = useGenericDialog();

	return (
		<ControlledAsyncDataGrid<PartConfigurationBomPricesApi_BomLineGroupPricesDto>
			columns={[
				textColumn({
					headerName: i18n.t("bom_line_group"),
					field: "bomLineGroupName",
					width: 300,
				}),
				floatColumn({
					headerName: formatLabelWithCurrencyCode(
						i18n.t("total_price"),
						partConfiguration.sellingCurrencyCode,
					),
					field: "totalPriceInSellingCurrency",
					width: 120,
					renderCell: ({ value }) =>
						value == null ? "" : formatMoneyWithCurrency(value, partConfiguration.sellingCurrencyCode),
				}),
				floatColumn({
					headerName: formatLabelWithCurrencyCode(
						i18n.t("total_cost"),
						partConfiguration.domesticCurrencyCode,
					),
					field: "totalCost",
					width: 150,
					renderCell: ({ value }) =>
						value == null ? "" : formatMoneyWithCurrency(value, partConfiguration.domesticCurrencyCode),
				}),
				showCostInSellingCurrency &&
					floatColumn({
						headerName: formatLabelWithCurrencyCode(
							i18n.t("total_cost"),
							partConfiguration.sellingCurrencyCode,
						),
						field: "totalCostInSellingCurrency",
						width: 180,
						renderCell: ({ value }) =>
							value == null ? "" : formatMoneyWithCurrency(value, partConfiguration.sellingCurrencyCode),
					}),
				floatColumn({
					headerName: i18n.t("sales_margin_percent"),
					field: "salesMarginPercent" as any,
					width: 150,
					valueGetter: (_, row) => calculateGroupSalesMarginPercent(row),
				}),
			]}
			onRowDoubleClick={({ row }) => {
				openGroup(row);
			}}
			actionBarComponents={
				<>
					<AavoButton
						label={i18n.t("drill")}
						icon={faArrowDown}
						disabled={onlySelectedRow == null}
						onClick={() => openGroup(onlySelectedRow!)}
						variant="outlined"
					/>
				</>
			}
			agentContext={{
				key: defineAgentUiContextKey("erp.configurator.partConfigurationBomPriceGroups.grid"),
				data: {
					partConfigurationId: partConfiguration.partConfigurationId,
				},
				rowFields: ["bomLineGroupId", "bomLineGroupName"],
				selectedRowPathSegment: (row) => row.bomLineGroupName,
			}}
			{...dataGridProps}
		/>
	);

	function calculateGroupSalesMarginPercent(row: PartConfigurationBomPricesApi_BomLineGroupPricesDto): number | null {
		const cost = useDomesticCostForMargin ? row.totalCost : row.totalCostInSellingCurrency;
		if (cost == null) return null;

		return calculateSalesMarginPercent({
			cost,
			salesPrice: row.totalPriceInSellingCurrency,
		});
	}

	function openGroup(row: PartConfigurationBomPricesApi_BomLineGroupPricesDto) {
		openDialog(() => ({
			title: row.bomLineGroupName,
			content: (
				<PartConfigurationBomPricesOfGroupDataGrid
					bomLineGroupId={row.bomLineGroupId}
					partConfiguration={partConfiguration}
					vatPercent={vatPercent}
				/>
			),
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.partConfigurationBomPriceGroup.dialog"),
				title: row.bomLineGroupName,
				pathSegments: [row.bomLineGroupName],
				data: {
					partConfigurationId: partConfiguration.partConfigurationId,
					bomLineGroupId: row.bomLineGroupId,
				},
			},
		}));
	}
};
